import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AdminPushSubscribersCard from "./AdminPushSubscribersCard";
import {
  Wallet,
  Users,
  Sun,
  Calendar,
  FileText,
  Wrench,
  CreditCard,
  ArrowRight,
  Plus,
  ChevronRight,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTHS_SHORT = ["Sty", "Lut", "Mar", "Kwi", "Maj", "Cze", "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru"];
const MONTHS_FULL = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];

const fmtPLN = (n) => {
  const num = Number(n || 0);
  return num.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " zł";
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

// ---------- KPI CARD ----------
function KpiCard({ label, value, subtitle, Icon, iconBg, iconColor, trend, testId }) {
  return (
    <div
      className="bg-white border border-border rounded-md p-5 shadow-sm hover:shadow-md transition-shadow"
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-md flex items-center justify-center shrink-0 ${iconBg}`}>
          <Icon className={`w-6 h-6 ${iconColor}`} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
            {label}
          </div>
          <div className="text-2xl md:text-3xl font-bold text-foreground mt-1 truncate">
            {value}
          </div>
          {subtitle && (
            <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
          )}
          {trend !== null && trend !== undefined && (
            <div className={`text-xs font-medium mt-0.5 flex items-center gap-1 ${trend >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              <span>{trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}%</span>
              <span className="text-muted-foreground">vs poprzedni</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- SECTION CARD ----------
function SectionCard({ title, badge, footer, footerTo, children, testId }) {
  return (
    <div className="bg-white border border-border rounded-md p-5 shadow-sm flex flex-col" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
          {title}
        </h3>
        {badge != null && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-red-100 text-red-600">
            {badge}
          </span>
        )}
      </div>
      <div className="flex-1">{children}</div>
      {footer && (
        <button
          onClick={footerTo}
          className="text-xs font-medium text-blue-600 hover:text-blue-700 mt-4 flex items-center gap-1"
        >
          {footer} <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ---------- LIST ROW ----------
function ListRow({ Icon, iconBg, iconColor, title, subtitle, right }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon className={`w-4 h-4 ${iconColor}`} strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
      </div>
      {right && <div className="text-right shrink-0">{right}</div>}
    </div>
  );
}

// ---------- MAIN ----------
export default function DashboardHome({ onNavigate, selectedYear, selectedMonth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartYear, setChartYear] = useState(selectedYear || new Date().getFullYear());
  const [quickModal, setQuickModal] = useState(null); // 'premie' | 'pozyczka' | null
  const [employees, setEmployees] = useState([]);
  const [modalForm, setModalForm] = useState({});
  const [modalBusy, setModalBusy] = useState(false);

  const openQuickModal = async (kind) => {
    setQuickModal(kind);
    const now = new Date();
    setModalForm({
      employee_id: '',
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      period: now.getDate() <= 15 ? '01-15' : '16-30',
      amount: '',
      note: '',
    });
    // Load employees list
    try {
      const token = localStorage.getItem("access_token");
      const { data: emps } = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(emps || []);
    } catch (e) {
      console.error('employees load failed', e);
    }
  };

  const closeQuickModal = () => { setQuickModal(null); setModalForm({}); };

  const submitQuickModal = async () => {
    if (!modalForm.employee_id || !modalForm.amount) return;
    setModalBusy(true);
    try {
      const token = localStorage.getItem("access_token");
      if (quickModal === 'premie') {
        await axios.post(`${API}/salary/quick-premie`, {
          employee_id: modalForm.employee_id,
          year: parseInt(modalForm.year),
          month: parseInt(modalForm.month),
          period: modalForm.period,
          amount: parseFloat(modalForm.amount),
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else if (quickModal === 'pozyczka') {
        const year = parseInt(modalForm.year);
        const month = parseInt(modalForm.month);
        // Reference date = last day of chosen period (helps ±1 month validation)
        const dayStr = modalForm.period === '01-15' ? '15' : '30';
        await axios.post(`${API}/employee-pozyczka`, {
          employee_id: modalForm.employee_id,
          data: `${year}-${String(month).padStart(2, '0')}-${dayStr}`,
          kwota: parseFloat(modalForm.amount),
          target_year: year,
          target_month: month,
          target_period: modalForm.period,
          note: modalForm.note || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      closeQuickModal();
      alert('Dodano pomyślnie.');
    } catch (e) {
      // Pydantic returns `detail` as an array of {msg, loc, ...} — normalize
      const d = e.response?.data?.detail;
      let msg;
      if (Array.isArray(d)) {
        msg = d.map((it) => it?.msg || JSON.stringify(it)).join('\n');
      } else if (typeof d === 'string') {
        msg = d;
      } else if (d) {
        msg = JSON.stringify(d);
      } else {
        msg = e.message || 'Błąd zapisu';
      }
      alert(msg);
    } finally {
      setModalBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const y = selectedYear || new Date().getFullYear();
      const m = selectedMonth || new Date().getMonth() + 1;
      const res = await axios.get(`${API}/dashboard/home?year=${y}&month=${m}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error("Dashboard home load failed", e);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh admin KPIs — new drafts / registrations / salaries appear fresh.
  useAutoRefresh(load);

  if (loading) {
    return (
      <div className="p-8 text-sm text-muted-foreground" data-testid="dashboard-loading">
        Ładowanie danych…
      </div>
    );
  }

  const kpi = data?.kpi || {};
  const chart = (data?.salary_chart || []).map((r) => ({
    ...r,
    label: MONTHS_SHORT[r.month - 1],
  }));
  const wymagajace = data?.wymagajace_akcji || [];
  const terminy = data?.najblizsze_terminy || [];
  const recentDocs = data?.recent_documents || [];
  const topEmp = data?.top_employees || [];
  const fin = data?.finansy || {};

  const urlop = kpi.najblizszy_urlop;
  const urlopValue = urlop ? (urlop.days_left === 0 ? "Dziś" : `Za ${urlop.days_left} dni`) : "Brak";
  const urlopSubtitle = urlop
    ? `${urlop.employee_login || urlop.employee_name || "?"} — ${fmtDate(urlop.date)}`
    : "Brak zaplanowanych";

  return (
    <div className="space-y-4" data-testid="dashboard-home">
      {/* Period pill + quick action buttons */}
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <div className="flex gap-2">
          <button
            onClick={() => openQuickModal('premie')}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-md text-sm font-semibold shadow-sm"
            data-testid="quick-add-premie-btn"
          >
            <Plus className="w-4 h-4" weight="bold" /> Dodaj premię
          </button>
          <button
            onClick={() => openQuickModal('pozyczka')}
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-md text-sm font-semibold shadow-sm"
            data-testid="quick-add-pozyczka-btn"
          >
            <Plus className="w-4 h-4" weight="bold" /> Dodaj pożyczkę
          </button>
        </div>
        <div className="inline-flex items-center gap-2 bg-white border border-border rounded-md px-4 py-2 shadow-sm">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            01 - {new Date(selectedYear || new Date().getFullYear(), selectedMonth || new Date().getMonth() + 1, 0).getDate()} {MONTHS_FULL[(selectedMonth || new Date().getMonth() + 1) - 1]} {selectedYear || new Date().getFullYear()}
          </span>
        </div>
      </div>

      {/* Quick add modal */}
      {quickModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4" data-testid="quick-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">
                {quickModal === 'premie' ? 'Dodaj premię' : 'Dodaj pożyczkę'}
              </h3>
              <button onClick={closeQuickModal} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Pracownik</label>
              <select
                value={modalForm.employee_id || ''}
                onChange={(e) => setModalForm((p) => ({ ...p, employee_id: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="quick-modal-emp"
              >
                <option value="">— wybierz —</option>
                {employees.map((e) => (
                  <option key={e._id || e.id} value={e._id || e.id}>{e.name || e.imie_nazwisko}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Rok</label>
                <select
                  value={modalForm.year || ''}
                  onChange={(e) => setModalForm((p) => ({ ...p, year: parseInt(e.target.value) }))}
                  className="w-full px-2 py-2 border border-slate-300 rounded-md text-sm"
                >
                  {[0, -1, -2].map((d) => {
                    const y = new Date().getFullYear() + d;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Miesiąc</label>
                <select
                  value={modalForm.month || ''}
                  onChange={(e) => setModalForm((p) => ({ ...p, month: parseInt(e.target.value) }))}
                  className="w-full px-2 py-2 border border-slate-300 rounded-md text-sm"
                >
                  {MONTHS_FULL.map((n, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1} — {n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Okres</label>
                <select
                  value={modalForm.period || '01-15'}
                  onChange={(e) => setModalForm((p) => ({ ...p, period: e.target.value }))}
                  className="w-full px-2 py-2 border border-slate-300 rounded-md text-sm"
                >
                  <option value="01-15">01-15</option>
                  <option value="16-30">16-30</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Kwota (zł)</label>
              <input
                type="number"
                step="0.01"
                value={modalForm.amount || ''}
                onChange={(e) => setModalForm((p) => ({ ...p, amount: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
                placeholder="0.00"
                data-testid="quick-modal-amount"
              />
            </div>

            {quickModal === 'pozyczka' && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Opis (opcjonalnie)</label>
                <input
                  type="text"
                  value={modalForm.note || ''}
                  onChange={(e) => setModalForm((p) => ({ ...p, note: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={closeQuickModal}
                className="flex-1 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md text-sm font-semibold"
              >
                Anuluj
              </button>
              <button
                onClick={submitQuickModal}
                disabled={modalBusy || !modalForm.employee_id || !modalForm.amount}
                className={`flex-1 px-4 py-2 text-white rounded-md text-sm font-semibold disabled:opacity-50 ${
                  quickModal === 'premie' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'
                }`}
                data-testid="quick-modal-save"
              >
                {modalBusy ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPI Grid (4 cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testId="kpi-do-wyplaty"
          label="Do wypłaty"
          value={fmtPLN(kpi.do_wyplaty)}
          subtitle={null}
          Icon={Wallet}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          trend={kpi.do_wyplaty_change_pct}
        />
        <KpiCard
          testId="kpi-pracownicy"
          label="Pracownicy"
          value={kpi.pracownicy_count ?? "—"}
          subtitle="Aktywnych"
          Icon={Users}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
        <KpiCard
          testId="kpi-urlop"
          label="Najbliższy urlop"
          value={urlopValue}
          subtitle={urlopSubtitle}
          Icon={Sun}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
        />
        <KpiCard
          testId="kpi-raty"
          label="Raty zaległych"
          value={kpi.raty_zaleglych ?? 0}
          subtitle="w bieżącym okresie"
          Icon={Calendar}
          iconBg="bg-red-50"
          iconColor="text-red-600"
        />
      </div>

      {/* Middle: chart (2 cols) + Wymagajace + Terminy */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Salary chart */}
        <div
          className="bg-white border border-border rounded-md p-5 shadow-sm lg:col-span-2"
          data-testid="salary-chart-card"
        >
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
              Wynagrodzenia w {chartYear}
            </h3>
            <select
              value={chartYear}
              onChange={(e) => setChartYear(Number(e.target.value))}
              className="text-xs border border-border rounded-md px-2 py-1 bg-white"
              data-testid="chart-year-select"
            >
              {Array.from({ length: 2100 - 2024 + 1 }, (_, i) => 2024 + i).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Suma wypłat w tym roku</p>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={chart} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#64748B" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                />
                <Tooltip
                  formatter={(v) => [fmtPLN(v), "Suma"]}
                  labelFormatter={(l, p) => (p && p[0] ? `${MONTHS_FULL[p[0].payload.month - 1]} ${chartYear}` : l)}
                  contentStyle={{
                    background: "#0F172A",
                    border: "none",
                    borderRadius: 6,
                    color: "#fff",
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#2563EB"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#2563EB" }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Wymagajace akcji */}
        <SectionCard
          testId="wymagajace-card"
          title="Wymagające akcji"
          badge={wymagajace.length || null}
          footer="Zobacz wszystkie wymagające akcji"
          footerTo={() => onNavigate && onNavigate("obligations")}
        >
          {wymagajace.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak spraw wymagających akcji.</p>
          ) : (
            wymagajace.slice(0, 3).map((it, i) => (
              <ListRow
                key={i}
                Icon={it.type === "kredyt" ? CreditCard : Wrench}
                iconBg={it.type === "kredyt" ? "bg-emerald-50" : "bg-amber-50"}
                iconColor={it.type === "kredyt" ? "text-emerald-600" : "text-amber-600"}
                title={it.title}
                subtitle={it.subtitle}
                right={
                  <div className="text-xs">
                    <div className="text-muted-foreground">Termin:</div>
                    <div className="font-semibold text-red-600">{fmtDate(it.deadline)}</div>
                  </div>
                }
              />
            ))
          )}
        </SectionCard>

        {/* Najblizsze terminy */}
        <SectionCard
          testId="terminy-card"
          title="Najbliższe terminy"
          footer="Zobacz kalendarz zespołu"
          footerTo={() => onNavigate && onNavigate("urlopy")}
        >
          {terminy.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak nadchodzących terminów.</p>
          ) : (
            terminy.slice(0, 3).map((it, i) => (
              <ListRow
                key={i}
                Icon={it.type === "kredyt" ? CreditCard : Calendar}
                iconBg="bg-blue-50"
                iconColor="text-blue-600"
                title={it.title}
                subtitle={it.subtitle}
                right={
                  <div className="text-xs">
                    <div className="font-semibold text-foreground">{fmtDate(it.date)}</div>
                    <div className="text-blue-600">za {it.days_left} dni</div>
                  </div>
                }
              />
            ))
          )}
        </SectionCard>
      </div>

      {/* Bottom: 3 cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Najnowsze dokumenty */}
        <SectionCard
          testId="docs-card"
          title="Najnowsze dokumenty"
          footer="Zobacz wszystkie dokumenty"
          footerTo={() => onNavigate && onNavigate("documents")}
        >
          {recentDocs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak dokumentów.</p>
          ) : (
            recentDocs.slice(0, 4).map((d, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${d.ext === "XLSX" ? "bg-emerald-50" : "bg-red-50"}`}>
                  <FileText className={`w-4 h-4 ${d.ext === "XLSX" ? "text-emerald-600" : "text-red-600"}`} strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    dodano {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("pl-PL") : ""}
                  </div>
                </div>
                <span className="text-[10px] font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-600">
                  {d.ext}
                </span>
              </div>
            ))
          )}
        </SectionCard>

        {/* Top pracownicy */}
        <SectionCard
          testId="top-emp-card"
          title={`Top pracownicy (${MONTHS_FULL[(selectedMonth || new Date().getMonth() + 1) - 1]})`}
          footer="Zobacz wszystkich pracowników"
          footerTo={() => onNavigate && onNavigate("employees")}
        >
          {topEmp.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak wypłat w tym miesiącu.</p>
          ) : (
            topEmp.map((e) => (
              <div key={e.rank} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${
                  e.rank === 1 ? "bg-amber-100 text-amber-700" : e.rank === 2 ? "bg-slate-100 text-slate-700" : "bg-orange-100 text-orange-700"
                }`}>
                  {e.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">{e.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{e.login}</div>
                </div>
                <div className="text-sm font-bold text-emerald-600 shrink-0">{fmtPLN(e.amount)}</div>
              </div>
            ))
          )}
        </SectionCard>

        {/* Podsumowanie finansów */}
        <SectionCard
          testId="fin-summary-card"
          title="Podsumowanie finansów"
          footer="Zobacz szczegółowy raport"
          footerTo={() => onNavigate && onNavigate("dochod")}
        >
          <div className="space-y-3 py-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Przychód w {MONTHS_FULL[(selectedMonth || new Date().getMonth() + 1) - 1].toLowerCase()}</span>
              <span className="text-sm font-bold text-foreground">{fmtPLN(fin.przychod)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Koszty w {MONTHS_FULL[(selectedMonth || new Date().getMonth() + 1) - 1].toLowerCase()}</span>
              <span className="text-sm font-bold text-foreground">{fmtPLN(fin.koszty)}</span>
            </div>
            <div className="border-t border-border pt-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Zysk netto</span>
              <span className={`text-lg font-bold ${fin.zysk_netto >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPLN(fin.zysk_netto)}</span>
            </div>
          </div>
        </SectionCard>
      </div>

      <AdminPushSubscribersCard />
    </div>
  );
}
